import { Button, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFilterSelectOptions } from '../constants/status';
import { useDeviceType } from '../constants/device-type';
import { LoadingButton } from '@mui/lab';
import FormFooter from 'components/layout/form-footer';
import OWSelect from 'components/design/ow-select';
import OWForm from 'components/layout/ow-form';
import { FormProps } from 'types/form';
import { Terminal } from '../types/dtos';
import { ReactNode } from 'react';
import PasswordField from 'features/auth/components/password-field';
import { isEmpty } from 'utils/object-helpers';

export type TerminalFormProps = FormProps<Terminal> & {
    isEdit?: boolean;
};

type TerminalFormType = (props: TerminalFormProps) => ReactNode;

export const TerminalForm: TerminalFormType = ({
    onSubmit,
    formData: terminal,
    errors,
    onChange,
    onCancel,
    disableSubmit,
    isSaveLoading,
    isEdit = false,
}) => {
    const { t } = useTranslation();
    const FILTER_SELECT_OPTIONS = useFilterSelectOptions();

    return (
        <OWForm onSubmit={onSubmit}>
            <Typography variant="h5">{t('TERMINAL_FORM.HEADER')}</Typography>
            <Stack spacing={2}>
                <TextField
                    value={terminal.email}
                    onChange={(e) => onChange('email', e.target.value)}
                    placeholder={t('TERMINAL_FORM.PLACEHOLDER_EMAIL')}
                    label={t('TERMINAL_FORM.LABEL_EMAIL')}
                    required
                    autoFocus
                    type="text"
                    error={!!errors?.email}
                    helperText={errors?.email?.errorMessage}
                />
                <PasswordField
                    label={isEdit ? t('TERMINAL_FORM.LABEL_NEW_PASSWORD') : t('TERMINAL_FORM.LABEL_PASSWORD')}
                    placeholder={
                        isEdit ? t('TERMINAL_FORM.PLACEHOLDER_NEW_PASSWORD') : t('TERMINAL_FORM.PLACEHOLDER_PASSWORD')
                    }
                    required={!isEdit}
                    value={terminal.password}
                    onChange={(e) => onChange('password', e.target.value)}
                    error={errors?.password}
                />
            </Stack>
            <TextField
                value={terminal.name}
                onChange={(e) => onChange('name', e.target.value)}
                placeholder={t('TERMINAL_FORM.PLACEHOLDER_NAME')}
                label={t('TERMINAL_FORM.LABEL_NAME')}
                required
                type="text"
                error={!!errors?.name}
                helperText={errors?.name?.errorMessage}
            />
            <Stack direction="row" spacing={2}>
                <OWSelect
                    value={terminal.status}
                    items={FILTER_SELECT_OPTIONS}
                    label={t('TERMINAL_FORM.LABEL_STATUS')}
                    labelId="label-status"
                    error={!!errors?.status}
                    onChange={(e) => onChange('status', e.target.value)}
                />
                <OWSelect
                    value={terminal.deviceType}
                    items={Object.keys(useDeviceType()).map((key) => ({
                        label: t(`TERMINAL_FORM.DEVICE_TYPE_${key}`),
                        value: key,
                    }))}
                    onChange={(e) => {
                        onChange('deviceType', e.target.value);
                    }}
                    label={t('TERMINAL_FORM.LABEL_DEVICE_TYPE')}
                    labelId="label-device-type"
                    error={!!errors?.deviceType}
                />
            </Stack>
            <TextField
                value={terminal.deviceId}
                onChange={(e) => onChange('deviceId', e.target.value)}
                placeholder={t('TERMINAL_FORM.PLACEHOLDER_DEVICE_ID')}
                label={t('TERMINAL_FORM.LABEL_DEVICE_ID')}
                required
                type="text"
                error={!!errors?.deviceId}
                helperText={errors?.deviceId?.errorMessage}
            />
            <TextField
                value={terminal.description}
                onChange={(e) => onChange('description', e.target.value)}
                placeholder={t('TERMINAL_FORM.PLACEHOLDER_DESCRIPTION')}
                label={t('TERMINAL_FORM.LABEL_DESCRIPTION')}
                type="text"
                error={!!errors?.description}
                helperText={errors?.description?.errorMessage}
                multiline
                minRows={3}
            />
            <FormFooter>
                <Stack direction="row" width="360px" spacing={3}>
                    <Button variant="outlined" onClick={onCancel}>
                        {t('TERMINAL_FORM.CANCEL_BUTTON')}
                    </Button>
                    <LoadingButton loading={isSaveLoading} type="submit" disabled={!isEmpty(errors) || disableSubmit}>
                        {t('TERMINAL_FORM.SAVE_BUTTON')}
                    </LoadingButton>
                </Stack>
            </FormFooter>
        </OWForm>
    );
};
